import {useState, useRef} from "react";
import TextareaAutosize from 'react-textarea-autosize';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Button } from '../ui/button';
//import { languages } from '../../utils/languages';
import { googleSupportedLanguages } from '../../services/languageService';
import FileUpload from '../common/FileUpload';
import SelectWithSearch from '../ui/SelectWithSearch';  
import plusIconBlack from '../../assets/icons/plus_icon_black.svg';

const SegmentsPanel = ({
  segments,
  sourceLanguage,
  targetLanguage,
  onSegmentChange,
  onRemoveSegment,
  onAddSegment,
  onFileUpload,
  evaluationId,
  onSourceLanguageChange,
  onTargetLanguageChange,
  isSourceAutoDetected,
  isTargetAutoDetected,
  onPaste,
  setIsSourceAutoDetected,
  setIsTargetAutoDetected
}) => {
  const [visibleCrossIconIndex, setVisibleCrossIconIndex] = useState(null);
  const [hoveredCrossIconIndex, setHoveredCrossIconIndex] = useState(null);
  const [uploadedSegments, setUploadedSegments] = useState([]);
  const [visibleSegments, setVisibleSegments] = useState(10);
  const [hasUploadedFile, setHasUploadedFile] = useState(false);
  const fileUploadRef = useRef();

  const handleFileSelect = (data) => {
    if (data && data.length > 0) {
      const newSegments = data
        .filter(item => item.source?.trim() || item.target?.trim())
        .map((item, index) => ({
          id: index + 1,
          serverId: null,
          source: item.source || '',
          target: item.target || ''
        }));

      if (newSegments.length > 0) {
        setUploadedSegments(newSegments);
        onFileUpload(newSegments);
        setHasUploadedFile(true);
        setVisibleSegments(10);
      }
    } else {
      setUploadedSegments([]);
      setHasUploadedFile(false);
      setVisibleSegments(10);
      onFileUpload([]);
    }
  };

  const handleShowMore = () => {
    setVisibleSegments(prev => prev + 10);
  };

  const renderSegments = () => {
    const segmentsToRender = uploadedSegments.length > 0 ? uploadedSegments : segments;
    const isCSVUploaded = uploadedSegments.length > 0;
    const segmentsToShow = isCSVUploaded ? segmentsToRender.slice(0, visibleSegments) : segmentsToRender;
    
    return segmentsToShow.map((segment, i) => {
      const isVisibleIcon = visibleCrossIconIndex === i;
      const isHoveredCrossIcon = hoveredCrossIconIndex === i;

      return (
        <div key={segment.id} className="grid grid-cols-2 relative -mb-[1px]">
          <div className="relative">
            <TextareaAutosize 
              value={segment.source}
              onChange={(e) => !isCSVUploaded && onSegmentChange(segment.id, 'source', e.target.value)}
              onPaste={(e) => !isCSVUploaded && onPaste(segment.id, 'source', e)}
              placeholder="Enter source text here..."
              className={`
                w-full h-[110px] min-h-[99%] resize-none
                p-[19px] text-base leading-6
                border border-t border-r-0
                border-t-transparent
                focus:outline-none focus:z-10
                focus:border-t-[#64748B] focus:border-[#64748B] focus:border-r
                ${isHoveredCrossIcon ? "border-[#F9B4B4] focus:border-[#F9B4B4] !border-t-[#F9B4B4]" : ""}
                ${isCSVUploaded ? "bg-gray-50 cursor-not-allowed" : ""}
                placeholder-gray-500
              `}
              minRows={3}
              onMouseEnter={() => !isCSVUploaded && setVisibleCrossIconIndex(i)}
              onMouseLeave={() => !isCSVUploaded && setVisibleCrossIconIndex(null)}
              readOnly={isCSVUploaded}
            />
            <div className="absolute bottom-1 right-2 text-gray-400 text-base leading-6">
              {segment.source.length}
            </div>
          </div>
          <div className="relative">
            <TextareaAutosize 
              value={segment.target}
              onChange={(e) => !isCSVUploaded && onSegmentChange(segment.id, 'target', e.target.value)}
              onPaste={(e) => !isCSVUploaded && onPaste(segment.id, 'target', e)}
              placeholder="Enter translation text here..."
              className={`
                focus:z-10 
                focus:border-[#64748B] 
                min-h-[99%] p-[19px] w-full h-[110px] 
                border 
                border-t border-t-transparent 
                resize-none focus:outline-none 
                ${isHoveredCrossIcon ? "z-10 border-[#F9B4B4] focus:border-[#F9B4B4] !border-t-[#F9B4B4]" : ""} 
                ${isCSVUploaded ? "bg-gray-50 cursor-not-allowed" : ""}
                focus:border-t-[#64748B]
                placeholder-gray-500
              `}
              minRows={3}
              onMouseEnter={() => !isCSVUploaded && setVisibleCrossIconIndex(i)}
              onMouseLeave={() => !isCSVUploaded && setVisibleCrossIconIndex(null)}
              readOnly={isCSVUploaded}
            />
            <div className="absolute bottom-1 right-2 text-gray-400 text-base leading-6">
              {segment.target.length}
            </div>
          </div>
          {!isCSVUploaded && (
            <button 
              onMouseEnter={() => {
                setVisibleCrossIconIndex(i);
                setHoveredCrossIconIndex(i);
              }}
              onMouseLeave={() => {
                setVisibleCrossIconIndex(null);
                setHoveredCrossIconIndex(null);
              }}
              onClick={() => onRemoveSegment(segment.id)}
              className={`${segments.length > 1 && isVisibleIcon ? "opacity-100 " : "opacity-0 "} [transition:0.2s_ease-in] z-20 absolute top-2/4 -translate-y-1/2 -right-[18px] p-1 bg-white border border-[#E2E8F0] rounded-full shadow-sm hover:bg-[#F1F5F9] hover:border-[#64748B]`}
            >
              <Cross2Icon className="h-4 w-4 text-[#64748B]"/>
            </button>
          )}
        </div>
      );
    });
  };

  return (
    <>
    <div className="flex flex-wrap items-center text-[#6B7280] mb-8">
    <span className="whitespace-nowrap"> Enter your text or</span>
      <button
        type="button"
        className="w-auto flex items-center justify-center 
        text-blue-600 hover:underline bg-transparent border-0 shadow-none outline-none ring-0 focus:ring-0 
        px-1.5 py-0 whitespace-nowrap"
        onClick={() => fileUploadRef.current && fileUploadRef.current.open()}
      >
        upload a CSV
      </button>
      <span className="whitespace-nowrap">file to evaluate translation quality</span>
    </div>

    <div className={(hasUploadedFile ? "flex flex-col-reverse" : "space-y-4 md:space-y-6") + " w-full max-w-full md:p-0"}>
      <div className={(uploadedSegments.length !== 0 ? "rounded-[16px] border border-[#E5EAF2] bg-[#FFFFFF]" : "rounded-lg") + " w-full max-w-full"}>
        <div className="border grid rounded-tl-[0.5em] rounded-tr-[0.5em] grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 bg-gray-50 p-2 md:p-4 border-b w-full">
          <SelectWithSearch
              options={googleSupportedLanguages}
              onChange={onSourceLanguageChange}
              value={isSourceAutoDetected && sourceLanguage !== 'Detect language' ? (
                <span>
                  {sourceLanguage} <span style={{ opacity: 0.5 }}>(Autodetected)</span>
                </span>
              ) : sourceLanguage || ''}
              inputPlaceholder="Choose source text language"
              isSearch
              selectPlaceholder="Choose source text language"
              isSourceAutoDetected={isSourceAutoDetected}
          />
          <SelectWithSearch
              options={googleSupportedLanguages}
              onChange={onTargetLanguageChange}
              value={isTargetAutoDetected && targetLanguage !== 'Detect language' ? (
                <span>
                  {targetLanguage} <span style={{ opacity: 0.5 }}>(Autodetected)</span>
                </span>
              ) : targetLanguage || ''}
              inputPlaceholder="Choose target text language"
              isSearch
              selectPlaceholder="Choose target text language"
              isTargetAutoDetected={isTargetAutoDetected}
          />
        </div>

        {renderSegments()}

        {uploadedSegments.length === 0 && (
          <div className="flex items-center justify-center border border-t-[0] rounded-br-[0.5em] rounded-bl-[0.5em] bg-gray-50 p-2 md:p-4 w-full">
            <Button
              onClick={onAddSegment}
              className="w-full text-[rgb(17,24,39)] hover:text-[rgb(17,24,39)]/80 hover:bg-transparent bg-transparent border-0 shadow-none outline-none ring-0 focus:ring-0 md:w-auto"
            >
            <img src={plusIconBlack} alt="Plus icon" className="h-4 w-4 mr-2" />
              Add Segment
            </Button>
          </div>
        )}

        {uploadedSegments.length > 0 && uploadedSegments.length > visibleSegments && (
          <div className="p-2 md:p-3">
            <Button
              onClick={handleShowMore}
              variant="ghost"
              className="w-full flex items-center justify-center hover:bg-transparent"
            >
              Show More
            </Button>
          </div>
        )}
      </div>
      <FileUpload 
        className={uploadedSegments.length === 0 ? "mt-4 md:mt-6" : "!mb-4 !mt-0 md:!mb-6"} 
        ref={fileUploadRef}
        onFileSelect={handleFileSelect} 
        segments={segments}
        evaluationId={evaluationId}
        onSourceLanguageChange={onSourceLanguageChange}
        onTargetLanguageChange={onTargetLanguageChange}
        setIsSourceAutoDetected={setIsSourceAutoDetected}
        setIsTargetAutoDetected={setIsTargetAutoDetected}
      />
    </div>
    </>
  );
};

export default SegmentsPanel;
